import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmSaveChange, InviteUserPopup } from "../../../../../general/popup";
import CIcon from '@coreui/icons-react';
import { CButton, CCol, CForm, CRow, CLink } from '@coreui/react';
import { useDispatch, useSelector } from "react-redux";
import CFormGroup from "../../../../../migration/CFormGroup";
import CLabel from "../../../../../migration/CLabel";
import Select, { components } from 'react-select';
import { CInvalidFeedback } from "../../../../../migration/CInvalidFeedback";
import { Formik, ErrorMessage } from 'formik';
import { CInput, CTextarea } from "../../../../../migration/CInput";
import * as Yup from 'yup';
import { fetchAccountUsers, setManagers, setShowBlockAccountPopup, toggleAsideLimitationHit } from "../../../../../../actions/subscriber";
import { setAsideShow } from "../../../../../../actions/common";
import { ADD_USER } from "../../../../../../constants";
import { toastError } from "../../../../../../utils";
import { toast } from 'react-toastify';
import { callTokenApi } from "../../../../../../apiCaller";

const MenuList = (props) => {
    return (
        <components.MenuList {...props}>
            {props.children}
        </components.MenuList>
    );
};

const noOptionsMessage = () => {
    return (
        <span>
            No option.
        </span>
    );
};

const validationSchema = Yup.object().shape({
    isSendMail: Yup.boolean().oneOf([true], 'Selecting email delivery is required.'),
    emailList: Yup.array().when('isSendMail', {
        is: true,
        then: (schema) => schema
            .min(1, 'Please select at least one recipient')
            .required('Recipient selection is required when sending email'), // Technically covered by min(1)
        otherwise: (schema) => schema.notRequired(), // Not required if not sending email
    }),
    customSubject: Yup.string()
        .trim()
        .when('isSendMail', {
            is: true,
            then: (schema) => schema.required('Custom subject is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
    optionalMessage: Yup.string().trim()
});

const initialValues = {
    isSendMail: true,
    emailList: [],
    customSubject: '',
    optionalMessage: '',
};

export default function SendPreviewViaMail({ jobId }) {
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false);
    const owner = useSelector((state) => state.subscriber.owner);
    const managers = useSelector((state) => state.subscriber.managers);
    const activeAccount = useSelector((state) => state.subscriber.activeAccount);
    const { numberOfUsers: usedUsers, packageNumberOfUsers: maxUsers } = activeAccount;
    const [defaultPlaceholderTo, setDefaultPlaceholderTo] = useState('Select recipients');
    const [modalInviteUser, setModalInviteUser] = useState(false);
    const [validateInviteEmail, setValidateInviteEmail] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [privateLoading, setPrivateLoading] = useState(false);

    const emailsOfAccountUsers = useMemo(() => {
        return owner ? [owner, ...managers].map(user => ({
            label: user.email,
            value: user.email
        })) : [];
    }, [owner, managers]);

    useEffect(() => {
        dispatch(fetchAccountUsers(activeAccount.id));
    }, []); // eslint-disable-line

    const toggleModalInviteUser = useCallback(() => {
        if (usedUsers >= maxUsers && maxUsers !== -1) {
            dispatch(toggleAsideLimitationHit());
            dispatch(setAsideShow(true));
            return;
        }
        setModalInviteUser(prev => !prev);
        setValidateInviteEmail('');
    }, [dispatch, usedUsers, maxUsers]);

    const closeModalInviteUser = useCallback(() => {
        if (usedUsers >= maxUsers && maxUsers !== -1) {
            dispatch(toggleAsideLimitationHit());
            dispatch(setAsideShow(true));
            return;
        }
        setModalInviteUser(false);
        setValidateInviteEmail('');
    }, [dispatch, usedUsers, maxUsers]);
    
    const inviteUser = useCallback(() => {
        if (usedUsers >= maxUsers && maxUsers !== -1) {
            toggleModalInviteUser();
            dispatch(toggleAsideLimitationHit());
            dispatch(setAsideShow(true));
            return;
        }

        const validateSchema = Yup.object().shape({
            newUserEmail: Yup.string()
                .required('Email is required')
                .email('Email invalid')
                .test('checkSpecialCharacters', 'Email invalid', value =>
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)),
        });

        validateSchema
            .validate({ newUserEmail })
            .then(() => {
                setValidateInviteEmail('');

                if (activeAccount) {
                    const data = {
                        email: newUserEmail,
                        accountId: activeAccount.id,
                    };
                    setPrivateLoading(true);

                    callTokenApi(`${ADD_USER}`, 'POST', data).then((response) => {
                        setPrivateLoading(false);

                        if (response.status === 200) {
                            if (response.data.useracc) {
                                dispatch(setManagers([...managers, response.data.user]));
                            }

                            toast.success(response.data.message);
                            setNewUserEmail('');
                            toggleModalInviteUser();
                        } else {
                            response.data.accountBlocked
                                ? dispatch(setShowBlockAccountPopup(true))
                                : toastError(response);
                            toggleModalInviteUser();
                        }
                    });
                }
            })
            .catch(err => setValidateInviteEmail(err.message));
    }, [dispatch, managers, newUserEmail, activeAccount, usedUsers, maxUsers, toggleModalInviteUser]);

    const submitNewUserEmail = (e) => {
        if (e.keyCode === 13) {
            inviteUser();
        }
    };

    const handleNewUserEmail = (e) => {
        setNewUserEmail(e.target.value);
        setValidateInviteEmail('');
    };

    const onSubmit = useCallback(async (values) => {
        const {
            emailList,
            customSubject,
            optionalMessage,
        } = values;
        
        try {
            const response = await callTokenApi('client/ai-insight-jobs/run-send-preview', 'POST', {
                jobId,
                accountId: activeAccount.accountId,
                emailRecipients: JSON.stringify(emailList),
                emailSubject: customSubject?.trim() || '',
                emailCustomMessage: optionalMessage?.trim() || '',
            });

            if (response?.status === 200) {
                setShowModal(false);
                toast.success(`Send Mail Preview successfully.`);
            }
        } catch (error) {
            console.error('Send preview error:', error);
            toast.error('An error occurred while send preview. Please try again later.');
        }
    }, [activeAccount.accountId, jobId]);

    return (
        <>
            <div className="d-flex align-items-center pointer ml-auto gap-1" onClick={() => setShowModal(true)}>
                <CIcon icon="cilEnvelopeClosed" height={15} width={15} className="icon-ai-insight envelope" />
                <span className='pt-1'>Send</span>
            </div>

            <ConfirmSaveChange
                show={showModal}
                onClose={() => setShowModal(false)}
                title="Send Preview Via Email"
                dataForm={false}
                showBtnClose={false}
                className="send-preview"
            >
                <p>Choose recipients to receive this preview result via email.</p>

                <Formik
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    validationSchema={validationSchema}
                >
                    {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, setFieldValue }) => {
                        return (
                            <CForm onSubmit={handleSubmit} noValidate>
                                <CRow className="mt-3 gap-2 account-connected-detail schedule-detail">
                                    <CCol lg="12">
                                        <CFormGroup>
                                            <CLabel htmlFor="emailList">
                                                <div className="title">
                                                    <div>
                                                        <strong>Select Recipients: </strong>
                                                    </div>
                                                    Recipients must be users inside your ListenLayer account.
                                                </div>
                                            </CLabel>

                                            <Select
                                                id="emailList"
                                                classNamePrefix="react-select"
                                                isMulti
                                                name="emailList"
                                                options={emailsOfAccountUsers}
                                                noOptionsMessage={noOptionsMessage}
                                                value={values.emailList}
                                                onChange={(emails) => setFieldValue('emailList', emails || [])}
                                                placeholder="Select recipients"
                                                className={!!errors.emailList && touched.emailList ? 'is-invalid' : ''}
                                                components={{ MenuList }}
                                                isSearchable={defaultPlaceholderTo === 'Select recipients' ? false : true}
                                                onMenuOpen={() => setDefaultPlaceholderTo('Type here to filter the list')}
                                            />

                                            <CInvalidFeedback>
                                                <ErrorMessage name="emailList" />
                                            </CInvalidFeedback>

                                            <div className="btn-invite-user">
                                                <CLink onClick={toggleModalInviteUser}>Invite new users.</CLink>
                                            </div>
                                        </CFormGroup>
                                    </CCol>

                                    <CCol lg="12">
                                        <CFormGroup>
                                            <CLabel htmlFor="customSubject">
                                                <div className="title">
                                                    <div>
                                                        <strong>Custom Message: </strong>
                                                    </div>
                                                    Include a custom subject line for the delivered email, as well as an optional
                                                    message.
                                                </div>
                                            </CLabel>

                                            <CInput
                                                id="customSubject"
                                                name="customSubject"
                                                placeholder="Custom Subject"
                                                invalid={!!errors.customSubject && touched.customSubject}
                                                value={values.customSubject}
                                                onChange={(e) => setFieldValue('customSubject', e.target.value)}
                                            />
                                            <CInvalidFeedback>
                                                <ErrorMessage name="customSubject" />
                                            </CInvalidFeedback>
                                        </CFormGroup>

                                        <CFormGroup>
                                            <CTextarea
                                                id="optionalMessage"
                                                name="optionalMessage"
                                                placeholder="Optional message"
                                                className={`form-control ${!!errors.optionalMessage && touched.optionalMessage ? 'is-invalid' : ''}`}
                                                value={values.optionalMessage}
                                                onChange={handleChange}
                                                rows={5}
                                            />
                                        </CFormGroup>
                                    </CCol>

                                    <CCol lg="12">
                                        <CButton
                                            type="button"
                                            className="btn-primary"
                                            disabled={isSubmitting}
                                            onClick={() => handleSubmit()}
                                        >
                                            {isSubmitting ? (<span className="dots-waiting">Waiting</span>) : "Send"}
                                        </CButton>

                                        <CButton
                                            onClick={() => setShowModal(false)}
                                            className="btn btn-cancel rounded-pill cancel" 
                                        >
                                            Cancel
                                        </CButton>
                                    </CCol>
                                </CRow>
                            </CForm>
                        )
                    }}
                </Formik>

                <InviteUserPopup
                    show={modalInviteUser}
                    isLoading={privateLoading}
                    onClose={closeModalInviteUser}
                    event={inviteUser}
                    btnText={'Add User'}
                >
                    <h2>Invite New User</h2>
                    <p>
                        Add a new user to {activeAccount.name}. If they already have a ListenLayer login, we'll automatically grant them access and
                        send them an email notifucation. If they do not yet have a ListenLayer account, we will invite them to create one.
                    </p>

                    <div className='invite-user-form'>
                        <CLabel>New User's Email:</CLabel>
                        <CInput
                            value={newUserEmail}
                            onChange={handleNewUserEmail}
                            onKeyDown={submitNewUserEmail}
                            data-lpignore='true'
                            invalid={!!validateInviteEmail}
                        />
                        <CInvalidFeedback>{validateInviteEmail}</CInvalidFeedback>
                    </div>
                </InviteUserPopup>
            </ConfirmSaveChange>
        </>
    );
}